import { useState, useEffect, useCallback } from "react";

const API_BASE =
  window.__RUNTIME_CONFIG__?.apiBase ||
  process.env.REACT_APP_API_BASE ||
  "http://127.0.0.1:5000";

const token = () => localStorage.getItem("portalToken") || "";
const loggedInUser = () => {
  try { return JSON.parse(localStorage.getItem("portalUser") || "null"); }
  catch { return null; }
};

const ROLES = [
  { value: "admin", label: "Admin", color: "#6a0080", bg: "#f3e5f7",
    desc: "Full access — manage users, applicants, statuses, purchase orders and all settings",
    canSeeSalary: true },
  { value: "regional_manager", label: "Regional Manager", color: "#1565c0", bg: "#e3f2fd",
    desc: "Same rights as Admin — can view salary expectations, manage statuses and see placed candidates",
    canSeeSalary: true },
  { value: "project_manager", label: "Project Manager", color: "#2e7d32", bg: "#e8f5e9",
    desc: "Read-only access — salary hidden. Can only update status of placed candidates once contract has ended",
    canSeeSalary: false },
  { value: "unit_manager", label: "Mobile Unit Manager", color: "#37474f", bg: "#eceff1",
    desc: "Read-only access — salary hidden. Can only update status of placed candidates once contract has ended",
    canSeeSalary: false },
  { value: "account_manager", label: "Account Manager", color: "#00695c", bg: "#e0f2f1",
    desc: "Read-only access to their fleets — salary hidden everywhere (profiles, placement, exports and documents)",
    canSeeSalary: false },
  { value: "finance_manager", label: "Financial Manager", color: "#8d6e00", bg: "#fff8e1",
    desc: "View-only access to every candidate and placed staff member, salaries included — cannot change anything",
    canSeeSalary: true },
];

const ROLE_MAP = Object.fromEntries(ROLES.map((r) => [r.value, r]));

// These roles only see Placed candidates on fleets they're assigned to
// (every earlier pipeline stage stays fully visible regardless) — so only
// they need a fleet multi-select at all.
const FLEET_SCOPED_ROLES = ["regional_manager", "project_manager", "unit_manager", "account_manager"];

const inp = { padding: "8px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, width: "100%", boxSizing: "border-box", fontFamily: "inherit" };
const lbl = { display: "flex", flexDirection: "column", fontSize: 12, color: "#555", gap: 4 };

function RoleBadge({ role }) {
  const r = ROLE_MAP[role] || { label: role, color: "#555", bg: "#f5f5f5" };
  return <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: r.bg, color: r.color, border: `1px solid ${r.color}33`, whiteSpace: "nowrap" }}>{r.label}</span>;
}

function TempPasswordBanner({ password, onDismiss }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(password); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div style={{ background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 8, padding: "12px 14px", marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#7a5800", marginBottom: 6 }}>⚠️ Temporary password — share this with the user now, it won't be shown again</div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <code style={{ flex: 1, fontSize: 15, fontWeight: 700, letterSpacing: "0.05em", background: "#fff", border: "1px solid #ffe082", borderRadius: 6, padding: "6px 10px", color: "#5d4037" }}>{password}</code>
        <button onClick={copy} style={{ padding: "6px 14px", background: "#f57f17", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{copied ? "Copied!" : "Copy"}</button>
        <button onClick={onDismiss} style={{ padding: "6px 12px", background: "transparent", color: "#888", border: "1px solid #ddd", borderRadius: 6, cursor: "pointer", fontSize: 12 }}>Dismiss</button>
      </div>
    </div>
  );
}

function UserModal({ user, onClose, onSaved, isEdit }) {
  const [form, setForm] = useState(isEdit
    ? { first_name: user.first_name || "", last_name: user.last_name || "", role: user.role || "project_manager", is_active: user.is_active,
        fleet_ids: (user.assigned_fleets || []).map((f) => f.id) }
    : { first_name: "", last_name: "", email: "", role: "project_manager", password: "", fleet_ids: [] }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fleets, setFleets] = useState([]);
  const me = loggedInUser();
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const fleetScoped = FLEET_SCOPED_ROLES.includes(form.role);

  useEffect(() => {
    if (!fleetScoped) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/fleet`, { headers: { Authorization: `Bearer ${token()}` } });
        const d = await res.json();
        if (res.ok) setFleets(d.fleets || []);
      } catch { /* silent — the form still works, just without the picker populated */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleetScoped]);

  const toggleFleet = (fleetId) => {
    setForm((p) => {
      const has = p.fleet_ids.includes(fleetId);
      return { ...p, fleet_ids: has ? p.fleet_ids.filter((id) => id !== fleetId) : [...p.fleet_ids, fleetId] };
    });
  };

  const submit = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      const url = isEdit ? `${API_BASE}/api/users/${user.id}` : `${API_BASE}/api/users`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, { method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` }, body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      onSaved(data.user, data.temp_password);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 12, width: 460, maxWidth: "100%", padding: 24, boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{isEdit ? "Edit User" : "Add Staff User"}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#888" }}>✕</button>
        </div>
        <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <label style={lbl}>First name *<input value={form.first_name} onChange={set("first_name")} required style={inp} /></label>
            <label style={lbl}>Last name *<input value={form.last_name} onChange={set("last_name")} required style={inp} /></label>
          </div>
          {!isEdit && <label style={lbl}>Email address *<input type="email" value={form.email} onChange={set("email")} required style={inp} /></label>}
          <label style={lbl}>Role *
            <select value={form.role} onChange={set("role")} style={inp}>
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <span style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{ROLE_MAP[form.role]?.desc}</span>
          </label>
          {fleetScoped && (
            <div style={lbl}>
              Assigned Fleets
              <span style={{ fontSize: 11, color: "#888" }}>
                Controls which Placed candidates they can see — every earlier stage (Registered through Offer Letter Accepted) stays fully visible regardless. Leave blank to see no Placed candidates yet.
              </span>
              <div style={{ border: "1px solid #ddd", borderRadius: 6, padding: "8px 10px", maxHeight: 160, overflowY: "auto", marginTop: 4 }}>
                {fleets.length === 0 ? (
                  <span style={{ fontSize: 12, color: "#999" }}>Loading fleets…</span>
                ) : (
                  fleets.map((f) => (
                    <label key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "3px 0", cursor: "pointer" }}>
                      <input type="checkbox" checked={form.fleet_ids.includes(f.id)} onChange={() => toggleFleet(f.id)} />
                      {f.location_name ? `${f.location_name} — ${f.fleet_nr}` : f.fleet_nr}
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
          {!isEdit && (
            <label style={lbl}>Password (leave blank to auto-generate)
              <input type="password" value={form.password} onChange={set("password")} style={inp} placeholder="Min 8 characters" />
            </label>
          )}
          {isEdit && (
            <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#555", cursor: "pointer" }}>
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} style={{ width: 15, height: 15 }} disabled={user?.email === me?.email} />
              Account active
            </label>
          )}
          {error && <div style={{ background: "#ffebee", color: "#b71c1c", borderRadius: 6, padding: "8px 12px", fontSize: 13 }}>{error}</div>}
          <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: "10px 0", background: "#fff", color: "#555", border: "1px solid #ddd", borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ flex: 2, padding: "10px 0", background: saving ? "#ccc" : "#6a0080", color: "#fff", border: "none", borderRadius: 6, cursor: saving ? "not-allowed" : "pointer", fontWeight: 700, fontFamily: "inherit" }}>
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null); // { mode: 'add'|'edit', user? }
  const [tempPasswords, setTempPasswords] = useState({});
  const [resetResults, setResetResults] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const me = loggedInUser();

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/users`, { headers: { Authorization: `Bearer ${token()}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load users");
      setUsers(data.users || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (updated, tempPass) => {
    setUsers((p) => {
      const exists = p.find((u) => u.id === updated.id);
      return exists ? p.map((u) => u.id === updated.id ? updated : u) : [...p, updated];
    });
    setModal(null);
    if (tempPass) setTempPasswords((p) => ({ ...p, [updated.id]: tempPass }));
  };

  const handleResetPassword = async (user) => {
    try {
      const res = await fetch(`${API_BASE}/api/users/${user.id}/reset-password`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` }, body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Reset failed");
      if (data.temp_password) setTempPasswords((p) => ({ ...p, [user.id]: data.temp_password }));
      setResetResults((p) => ({ ...p, [user.id]: "✓ Reset" }));
      setTimeout(() => setResetResults((p) => { const n = { ...p }; delete n[user.id]; return n; }), 4000);
    } catch (err) { alert(err.message); }
  };

  const handleDelete = async (user) => {
    try {
      const res = await fetch(`${API_BASE}/api/users/${user.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token()}` } });
      if (!res.ok) throw new Error((await res.json()).error || "Delete failed");
      setUsers((p) => p.filter((u) => u.id !== user.id));
      setConfirmDelete(null);
    } catch (err) { alert(err.message); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Loading users…</div>;

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, sans-serif", paddingBottom: 40 }}>
      {modal && <UserModal user={modal.user} isEdit={modal.mode === "edit"} onClose={() => setModal(null)} onSaved={handleSaved} />}

      {confirmDelete && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 12, width: 380, padding: 24, boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
            <h3 style={{ margin: "0 0 10px", color: "#b71c1c", fontSize: 16 }}>Delete User</h3>
            <p style={{ fontSize: 13, color: "#555", margin: "0 0 20px", lineHeight: 1.5 }}>Permanently delete <strong>{confirmDelete.first_name} {confirmDelete.last_name}</strong>? This cannot be undone.</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirmDelete(null)} style={{ flex: 1, padding: "9px 0", background: "#fff", border: "1px solid #ddd", borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={() => handleDelete(confirmDelete)} style={{ flex: 1, padding: "9px 0", background: "#b71c1c", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 700, fontFamily: "inherit" }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1a1a1a" }}>Staff User Management</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#777" }}>Manage staff access to the Guud Vision Talent Pool Dashboard</p>
        </div>
        <button onClick={() => setModal({ mode: "add" })} style={{ padding: "9px 18px", background: "#6a0080", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13, fontFamily: "inherit" }}>+ Add User</button>
      </div>

      {error && <div style={{ background: "#ffebee", color: "#b71c1c", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 16 }}>{error}</div>}
      {Object.entries(tempPasswords).map(([uid, pass]) => (
        <TempPasswordBanner key={uid} password={pass} onDismiss={() => setTempPasswords((p) => { const n = { ...p }; delete n[uid]; return n; })} />
      ))}

      {/* Sections by role */}
      {ROLES.map((role) => {
        const roleUsers = users.filter((u) => u.role === role.value);
        return (
          <div key={role.value} style={{ marginBottom: 28 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: role.color }}>{role.label}s</h3>
              <span style={{ fontSize: 11, fontWeight: 700, background: role.bg, color: role.color, padding: "2px 8px", borderRadius: 10, border: `1px solid ${role.color}33` }}>{roleUsers.length}</span>
              <span style={{ fontSize: 12, color: "#999" }}>— {role.desc}</span>
              <span style={{ marginLeft: "auto", fontSize: 11, padding: "2px 8px", borderRadius: 4, background: role.canSeeSalary ? "#e8f5e9" : "#fff3e0", color: role.canSeeSalary ? "#2e7d32" : "#e65100", border: `1px solid ${role.canSeeSalary ? "#a5d6a7" : "#ffcc80"}` }}>
                {role.canSeeSalary ? "✓ Salary visible" : "✗ Salary hidden"}
              </span>
            </div>
            {roleUsers.length === 0 ? (
              <div style={{ background: "#fafafa", border: "1px solid #eee", borderRadius: 8, padding: "14px 16px", fontSize: 13, color: "#aaa" }}>No {role.label.toLowerCase()} users yet.</div>
            ) : (
              <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 10, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#fafafa", borderBottom: "1px solid #eee" }}>
                      {["Name", "Email", "Role", "Fleets", "Status", "Created", "Actions"].map((h) => (
                        <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {roleUsers.map((u) => (
                      <tr key={u.id} style={{ borderBottom: "1px solid #f5f5f5", opacity: u.is_active ? 1 : 0.5 }}>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{u.first_name} {u.last_name}</div>
                          {u.email === me?.email && <div style={{ fontSize: 10, color: "#9c27b0", fontWeight: 700 }}>YOU</div>}
                        </td>
                        <td style={{ padding: "10px 14px", fontSize: 13, color: "#555" }}>{u.email}</td>
                        <td style={{ padding: "10px 14px" }}><RoleBadge role={u.role} /></td>
                        <td style={{ padding: "10px 14px", fontSize: 12, color: "#555", maxWidth: 180 }}>
                          {FLEET_SCOPED_ROLES.includes(u.role)
                            ? ((u.assigned_fleets || []).length
                                ? u.assigned_fleets.map((f) => f.fleet_nr).join(", ")
                                : <span style={{ color: "#e65100" }}>None assigned</span>)
                            : "—"}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: u.is_active ? "#e8f5e9" : "#ffebee", color: u.is_active ? "#2e7d32" : "#b71c1c" }}>
                            {u.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td style={{ padding: "10px 14px", fontSize: 12, color: "#999" }}>{u.created_at ? new Date(u.created_at).toLocaleDateString("en-ZA") : "—"}</td>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                            <button onClick={() => setModal({ mode: "edit", user: u })} style={{ padding: "4px 10px", background: "#e3f2fd", color: "#1565c0", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>Edit</button>
                            <button onClick={() => handleResetPassword(u)} style={{ padding: "4px 10px", background: "#fff3e0", color: "#e65100", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>Reset Password</button>
                            {u.email !== me?.email && (
                              <button onClick={() => setConfirmDelete(u)} style={{ padding: "4px 10px", background: "#ffebee", color: "#b71c1c", border: "none", borderRadius: 5, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>Delete</button>
                            )}
                            {resetResults[u.id] && <span style={{ fontSize: 11, color: "#2e7d32", fontWeight: 700 }}>{resetResults[u.id]}</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* Permissions guide */}
      <div style={{ background: "#fafafa", border: "1px solid #eee", borderRadius: 10, padding: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>Role Permissions Guide</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 10 }}>
          {ROLES.map((role) => (
            <div key={role.value} style={{ background: role.bg, border: `1px solid ${role.color}33`, borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: role.color, marginBottom: 6 }}>{role.label}</div>
              <div style={{ fontSize: 11, color: "#555", lineHeight: 1.7 }}>{role.desc}</div>
              <div style={{ marginTop: 6, fontSize: 11, fontWeight: 700, color: role.canSeeSalary ? "#2e7d32" : "#e65100" }}>
                {role.canSeeSalary ? "✓ Can see salary expectations" : "✗ Salary expectations hidden"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
